import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sprout } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import fertiliserService from '../../services/fertiliser';
import { errorMessage } from '../../utils/apiResponse';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { METHOD_LABEL, addNutrients, lineNutrients, round } from '../../utils/fertiliser';
import type { FertiliserPlan } from '../../types/fertiliser';

const seasonText = (y: number) => `${y}–${String(y + 1).slice(2)}`;

/** This season's fertiliser plan for a planting: N, P and K against target, and each application. */
export default function FertiliserPlanPanel({ areaId }: { areaId: number }) {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [data, setData] = useState<{ season_year: number; plan: FertiliserPlan | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    fertiliserService.planForArea(areaId)
      .then((r) => { if (r.success && r.data) setData(r.data); })
      .catch((e) => setError(errorMessage(e, 'Could not load the fertiliser plan')));
  }, [areaId]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await fertiliserService.createPlan({ cultivar_area_id: areaId, season_year: data?.season_year });
      if (r.success && r.data) navigate(`/orchard/fertiliser/plans/${r.data.id}`);
    } catch (e) {
      setError(errorMessage(e, 'Could not start the plan'));
    } finally {
      setStarting(false);
    }
  };

  const plan = data?.plan ?? null;
  const totals = plan ? addNutrients(plan.lines.map((l) => lineNutrients(l, l, plan.area_hectares, plan.tree_count).nutrients)) : {};

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Sprout className="h-4 w-4 text-neutral-500 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-neutral-700">Fertiliser plan{data ? ` ${seasonText(data.season_year)}` : ''}</h3>
              <p className="text-xs text-neutral-500">What each hectare needs this season, and the applications that give it.</p>
            </div>
          </div>
          {plan
            ? <Button size="sm" variant="outline" onClick={() => navigate(`/orchard/fertiliser/plans/${plan.id}`)}>Open plan</Button>
            : data && <Button size="sm" variant="outline" isLoading={starting} onClick={start}>Start a plan</Button>}
        </div>
        {error && <p className="text-sm text-error-700">{error}</p>}
        {!data && !error && <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 text-neutral-400 animate-spin" /></div>}
        {data && !plan && <p className="text-sm text-neutral-500">No plan for this season yet.</p>}
        {plan && (
          <>
            <p className="text-sm text-neutral-700 flex flex-wrap gap-x-4 gap-y-1">
              {(['N', 'P', 'K'] as const).map((n) => (
                <span key={n}>
                  {n} <strong>{round(totals[n] ?? 0, 0)}</strong>{plan.targets[n] != null && ` of ${round(plan.targets[n], 0)}`} kg/ha
                </span>
              ))}
            </p>
            {plan.lines.length === 0
              ? <p className="text-sm text-neutral-500">No applications yet.</p>
              : (
                <ul className="divide-y divide-neutral-100">
                  {plan.lines.map((l) => (
                    <li key={l.id} className="py-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-sm">
                      <span className="text-neutral-800 break-words min-w-0">
                        {l.planned_on ? formatLocalDateInTz(l.planned_on, timezone) : 'No date'} · {l.product_name}
                      </span>
                      <span className="text-neutral-600">
                        {round(l.rate, 2)} {l.rate_unit} · {METHOD_LABEL[l.method]}
                        {l.used_recorded_at ? ' · applied' : l.job_id ? ` · job ${l.job_status ?? ''}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
